/**
 * Company details — the customer's contractual identity for the SaaS agreement (MSA/SOF/DPA).
 * See docs/adr/0015-agreement-esignature-architecture.md ("CompanyLegalProfile").
 *
 * Entered once by the org owner; PeopleOrbit staff creating an agreement have it pulled in
 * automatically. One row per organisation. `GET` returns a prefilled draft (`id: null`) when no
 * row exists yet. GSTIN/PAN/CIN are business identifiers, not personal PII.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { emailSchema, errorBodySchema, instantSchema, uuidSchema } from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

export const companyEntityTypeSchema = z.enum(['company', 'llp', 'firm', 'other']);
export type CompanyEntityType = z.infer<typeof companyEntityTypeSchema>;

const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

const upper = (v: unknown) => (typeof v === 'string' ? v.trim().toUpperCase() : v);
/** Blank strings become undefined so an emptied optional field is "not provided". */
const blankToUndefined = (v: unknown) => (typeof v === 'string' && v.trim() === '' ? undefined : v);
const optText = (max: number) => z.preprocess(blankToUndefined, z.string().trim().max(max).optional());
const optEmail = z.preprocess(blankToUndefined, emailSchema.optional());

export const companyProfileFieldsSchema = z.object({
  legalName: z.string().trim().min(1, 'Legal name is required').max(300),
  entityType: companyEntityTypeSchema.default('company'),
  registeredAddress: z.string().trim().min(1, 'Registered address is required').max(1_000),
  country: z.preprocess(upper, z.string().length(2, 'Use a 2-letter ISO country code')).default('IN'),
  gstState: optText(100),
  gstin: z.preprocess((v) => blankToUndefined(upper(v)), z.string().max(20).optional()),
  cin: z.preprocess((v) => blankToUndefined(upper(v)), z.string().max(30).optional()),
  pan: z.preprocess(
    (v) => blankToUndefined(upper(v)),
    z.string().regex(PAN_RE, 'PAN must be 10 characters, e.g. ABCDE1234F').optional(),
  ),
  billingContactName: optText(200),
  billingContactEmail: optEmail,
  billingContactPhone: optText(30),
  noticeEmail: optEmail,
  signatoryName: optText(200),
  signatoryEmail: optEmail,
  signatoryTitle: optText(200),
});

/** GSTIN is validated for India only, and only when provided. */
export const upsertCompanyProfileBodySchema = companyProfileFieldsSchema.superRefine((v, ctx) => {
  if (v.country === 'IN' && v.gstin && !GSTIN_RE.test(v.gstin)) {
    ctx.addIssue({ code: 'custom', path: ['gstin'], message: 'GSTIN must be 15 characters, e.g. 22AAAAA0000A1Z5' });
  }
});
export type UpsertCompanyProfileBody = z.infer<typeof upsertCompanyProfileBodySchema>;

/** True when every field needed to draw up an agreement is present (drives `completedAt`). */
export function isCompanyProfileComplete(v: {
  legalName?: string | null; registeredAddress?: string | null;
  signatoryName?: string | null; signatoryEmail?: string | null; signatoryTitle?: string | null;
}): boolean {
  return [v.legalName, v.registeredAddress, v.signatoryName, v.signatoryEmail, v.signatoryTitle]
    .every((x) => typeof x === 'string' && x.trim().length > 0);
}

export const companyProfileSchema = z.object({
  id: uuidSchema.nullable(),
  legalName: z.string(),
  entityType: companyEntityTypeSchema,
  registeredAddress: z.string(),
  country: z.string(),
  gstState: z.string().nullable(),
  gstin: z.string().nullable(),
  cin: z.string().nullable(),
  pan: z.string().nullable(),
  billingContactName: z.string().nullable(),
  billingContactEmail: z.string().nullable(),
  billingContactPhone: z.string().nullable(),
  noticeEmail: z.string().nullable(),
  signatoryName: z.string().nullable(),
  signatoryEmail: z.string().nullable(),
  signatoryTitle: z.string().nullable(),
  completedAt: instantSchema.nullable(),
});
export type CompanyProfile = z.infer<typeof companyProfileSchema>;

export const companyProfileContract = c.router({
  get: {
    method: 'GET',
    path: '/company-profile',
    responses: { 200: companyProfileSchema, 401: errorBodySchema, 403: errorBodySchema },
    summary: "Read the organisation's company details (a prefilled draft if none saved yet)",
    metadata: routeMeta('org.settings:read'),
  },
  upsert: {
    method: 'PUT',
    path: '/company-profile',
    body: upsertCompanyProfileBodySchema,
    responses: { 200: companyProfileSchema, 401: errorBodySchema, 403: errorBodySchema, 422: errorBodySchema },
    summary: "Save the organisation's company details used to prefill agreements",
    metadata: routeMeta('org.settings:update'),
  },
});
